import { Component, ViewChild } from '@angular/core';
import {
  SituAnalyticsWebApiClient, ListUsageSummariesRequest,
  ListUsageSummariesResponse, UsageSummaryType, UsageSummary
} from 'situanalytics-webapi-client';
import {  Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AuthService, BreadcrumbService, StateService, CompanyService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { ConfigService } from 'src/app/services/config.service';
import { formatDate } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { Chart } from 'chart.js';
import { MatDatepicker } from '@matheo/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'platform-usage',
  templateUrl: './platform-usage.component.html',
  styleUrls: ['./platform-usage.component.scss'],
})
export class PlatformUsageComponent extends SituBaseComponent {
  private readonly defaultColumns = [
    'company',
    'productCalloutsHomes',
    'productCalloutsProducts',
    'productCallouts',
    'renderQueueTime',
    'renderTime',
    'renderRequests',
    'viewFacadeRequests',
    'viewHomeRequests',
    'viewRoomRequests',
    'viewPlanRequests',
    'viewProductRequests',
    'viewRenderRequests',
    'viewVanityRequests',
    'viewVirtualRealityRequests',
    'viewConnectRequests',
    'storageConsumed',
    'storageConsumedTotal'
  ];
  viewControls = new ViewControls(1000, 1, 0, [1000]);

  public baseColorBlue: string = 'hsl(210, 90%, 55%)';
  public baseColorPink: string = 'hsl(330, 90%, 55%)';
  public baseColorGreen: string = 'hsl(145, 60%, 55%)';
  public baseColorOrange: string = 'hsl(30, 90%, 55%)';

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  usageSummaries: UsageSummary[] = [];
  filteredUsageSummaries: UsageSummary[] = [];
  usageSummaryType: UsageSummaryType = UsageSummaryType.Month;

  usageSummaryTypes = UsageSummaryType;
  reportId = '';
  isReportReady = false;
  reportErrorCount = 0;
  reportPollCount = 0;

  displayedColumns: string[] = this.defaultColumns;
  companyFilter: boolean = false;
  selectedChartCompany: string | undefined;
  currentUsageSummary: UsageSummary | undefined;

  @ViewChild (BaseChartDirective) chartCallout: BaseChartDirective | undefined;
  chartCalloutsLabels: string[] = [];
  chartCalloutsData: number[] = [];
  chartCalloutsColors: string[] = [];

  @ViewChild (BaseChartDirective) chartRender: BaseChartDirective | undefined;
  chartRendersLabels: string[] = [];
  chartRendersData: number[] = [];
  chartRendersColors: string[] = [];

  @ViewChild (BaseChartDirective) chartView: BaseChartDirective | undefined;
  chartViewsLabels: string[] = [];
  chartViewsData: number[] = [];
  chartViewsColors: string[] = [];

  @ViewChild (BaseChartDirective) chartStorage: BaseChartDirective | undefined;
  chartStorageLabels: string[] = [];
  chartStorageData: number[] = [];
  chartStorageColors: string[] = [];

  chartOptions = {
    responsive: true
  };

  dateFrom: Date = new Date();
  dateTo: Date = new Date();
  dateMonthFrom: Date = new Date();
  dateMonthTo: Date = new Date();
  dateYearFrom: Date = new Date();
  dateYearTo: Date = new Date();
  minDate = new Date(2023, 10, 1); // Nov 1, 2023 (month is 0-indexed)
  maxDate = new Date(); // Current date
  readonly renderTimeUnit = 'seconds';
  readonly viewRequestsUnit = 'views';
  readonly storageConsumedUnit = 'GB';

  pollForReportStatusSub: Subscription | undefined;

  /* Query Parameters */
  readonly MonthParamName: string = "month";
  readonly DateParamName: string = "date";
  readonly CompanyParamName: string = "companyId";
  readonly PeriodParamName: string = "period";
  /* Query Parameters */

  private originalCompanyId: string | undefined;
  private hasUserNavigatedMonth: boolean = false;

  // Store URL parameters for processing after auth is ready
  private urlCompanyParam: string | undefined;
  private urlPeriodParam: string | undefined;
  private urlDateParam: string | undefined;
  private urlMonthParam: string | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    public dialog: MatDialog,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private companyService: CompanyService) {
      super(authService, stateService);

      // Store URL parameters for processing after auth is ready
      this.urlCompanyParam = this.route.snapshot.queryParams[this.CompanyParamName];
      this.urlPeriodParam = this.route.snapshot.queryParams[this.PeriodParamName];
      this.urlDateParam = this.route.snapshot.queryParams[this.DateParamName];
      this.urlMonthParam = this.route.snapshot.queryParams[this.MonthParamName];

      // Only process non-company parameters in constructor
      if (this.urlPeriodParam) {
        this.setPeriodFromUrlParam(this.urlPeriodParam);
      }

      if (this.urlDateParam) {
        this.setDateFromUrlParam(this.urlDateParam);
      }
      
      if (this.urlMonthParam) {
        this.setMonthFromUrlParam(this.urlMonthParam);
      }
      
      if (!this.urlDateParam && !this.urlMonthParam) {
        // Initialize dates with proper UTC handling
        this.initializeUtcDates();
      }
      
      this.updateMaxDate();
  }

  protected onSelectedCompanyChange(): void {
    // Update URL parameters when company changes
    this.updateAllParams();
    
    // Load data for new company
    this.loadData();
  }
  
  getTotalViews(): number {
    if (!this.currentUsageSummary) return 0;
    return (
      (this.currentUsageSummary.viewHomeRequests || 0) +
      (this.currentUsageSummary.viewRoomRequests || 0) +
      (this.currentUsageSummary.viewFacadeRequests || 0) +
      (this.currentUsageSummary.viewPlanRequests || 0) +
      (this.currentUsageSummary.viewProductRequests || 0) +
      (this.currentUsageSummary.viewRenderRequests || 0) +
      (this.currentUsageSummary.viewVanityRequests || 0) +
      (this.currentUsageSummary.viewVirtualRealityRequests || 0) +
      (this.currentUsageSummary.viewConnectRequests || 0)
    );
  }
  
  getTimeDisplay(seconds: number | undefined): string {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  }
  
  getDataPeriodLabel(): string {
    return this.usageSummaryType === UsageSummaryType.Day ? 'Daily' : 'Monthly';
  }
  
  getDataTimestamp(): string {
    if (this.usageSummaryType === UsageSummaryType.Day) {
      return formatDate(this.dateFrom, 'd MMM, yyyy', 'en-US');
    } else {
      return formatDate(this.dateMonthFrom, 'MMM yyyy', 'en-US');
    }
  }
  
  getStoragePercentage(): number {
    if (!this.currentUsageSummary || !this.currentUsageSummary.storageConsumedTotal) {
      return 0;
    }
    const percentage = (this.currentUsageSummary.storageConsumed! / this.currentUsageSummary.storageConsumedTotal!) * 100;
    return Math.round(percentage * 10) / 10; // Round to 1 decimal place
  }

  ngOnInit(): void {
    super.ngOnInit();

    // Store the original company ID for URL parameter logic
    this.originalCompanyId = this.selectedCompany?.id;

    // Process URL parameters now that auth state is ready
    this.processUrlParameters();
    
    // Check if we came in via deep link and clean up URL after processing
    if (this.urlMonthParam || this.urlDateParam || this.urlPeriodParam || this.urlCompanyParam) {
      // Clear URL parameters after processing deep link
      setTimeout(() => {
        this.clearUrlParameters();
      }, 500);
    }
    
    this.loadData();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.pollForReportStatusSub) {
      this.pollForReportStatusSub.unsubscribe();
    }
  }

  convertKbToGb(kb: number): number {
    const gb = kb / (1024 * 1024);
    return parseFloat(gb.toFixed(1));
  }

  convertToCSV(objArray: any[], excludeColumns: string[] = []): string {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
    let str = '';
    let row = '';
  
    // Extract headers
    for (let index in objArray[0]) {
      if (!excludeColumns.includes(index)) {
        row += index + ',';
      }
    }
    row = row.slice(0, -1);
    str += row + '\r\n';
  
    // Extract data
    for (let i = 0; i < array.length; i++) {
      let line = '';
      for (let index in array[i]) {
        if (!excludeColumns.includes(index)) {
          if (line !== '') line += ',';
          line += array[i][index];
        }
      }
      str += line + '\r\n';
    }
    return str;
  }

  downloadCSV() {
    const columnsToExclude = ['companyId', 'timestamp', 'usageSummaryType'];
    const csvData = this.convertToCSV(this.filteredUsageSummaries, columnsToExclude);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const fileName = this.generateCsvFileName();
  
    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  getCurrentTimestamp(): string {
    const now = new Date();
    return now.toISOString().replace(/[\-\:\.]/g, '');
  }
  
  generateCsvFileName(): string {
    const companyCode = this.selectedCompany?.code || 'unknown';
    const timestamp = this.getCurrentTimestamp();
    
    if (this.usageSummaryType === UsageSummaryType.Day) {
      const dateStr = formatDate(this.dateFrom, 'yyyy-MM-dd', 'en-US');
      return `platform_usage_daily_${companyCode}_${dateStr}_${timestamp}.csv`;
    } else if (this.usageSummaryType === UsageSummaryType.Month) {
      const monthStr = formatDate(this.dateMonthFrom, 'yyyy-MM', 'en-US');
      return `platform_usage_monthly_${companyCode}_${monthStr}_${timestamp}.csv`;
    } else {
      return `platform_usage_${companyCode}_${timestamp}.csv`;
    }
  }

  onChartClick(event: any, chartType: string) {
    let chartInstance: Chart | undefined;
    switch(chartType) {
      case 'callout':
        if (this.chartCallout) chartInstance = this.chartCallout.chart;
        break;
      case 'render':
        if (this.chartRender) chartInstance = this.chartRender.chart;
        break;
      case 'view':
        if (this.chartView) chartInstance = this.chartView.chart;
        break;
      case 'storage':
        if (this.chartStorage) chartInstance = this.chartStorage.chart;
        break;
    }

    if (chartInstance && chartInstance.canvas) {
      const ctx = chartInstance.canvas.getContext('2d');
      if (ctx) {
        const activeElements = chartInstance.getElementAtEvent(event) as any;
        if (activeElements.length > 0) {
          const firstActiveElement = activeElements[0];
          const datasetIndex = firstActiveElement._datasetIndex;
          const index = firstActiveElement._index;

          let dataLabel = chartInstance.data.labels![index] as string;
          if (dataLabel) {
            let company = dataLabel.split(" (")[0];
            if (company) {
              this.selectedChartCompany = company;
            }
          }
        }
      }
    }
  }

  getDownloadButtonLabel(): string {
    const companyName = this.selectedCompany?.code || 'Company';
    
    if (this.usageSummaryType === UsageSummaryType.Day) {
      const dateStr = formatDate(this.dateFrom, 'd MMM, yyyy', 'en-US');
      return `Export ${companyName} Daily Usage (${dateStr})`;
    } else if (this.usageSummaryType === UsageSummaryType.Month) {
      const monthStr = formatDate(this.dateMonthFrom, 'MMM yyyy', 'en-US');
      return `Export ${companyName} Monthly Usage (${monthStr})`;
    } else if (this.usageSummaryType === UsageSummaryType.Year) {
      return `Export ${companyName} Yearly Usage (${this.dateYearTo.getFullYear()})`;
    }
    return `Export ${companyName} Usage Data`;
  }


  updateMaxDate(): void {
    // Set max date to yesterday UTC to account for UTC reporting times
    // This ensures we only show dates where data is likely available
    const nowUtc = new Date();
    const yesterdayUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth(), 
      nowUtc.getUTCDate() - 1
    ));
    this.maxDate = yesterdayUtc;
  }

  initializeUtcDates(): void {
    const nowUtc = new Date();
    
    // For daily: default to yesterday UTC
    const yesterdayUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth(), 
      nowUtc.getUTCDate() - 1,
      0, 0, 0, 0
    ));
    this.dateFrom = yesterdayUtc;
    this.dateTo = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth(), 
      nowUtc.getUTCDate() - 1,
      23, 59, 59, 999
    ));

    // For monthly: default to previous month UTC  
    const previousMonthUtc = new Date(Date.UTC(
      nowUtc.getUTCFullYear(), 
      nowUtc.getUTCMonth() - 1, 
      1
    ));
    this.dateMonthFrom = previousMonthUtc;
    
    // End of previous month UTC
    this.dateMonthTo = new Date(Date.UTC(
      previousMonthUtc.getUTCFullYear(), 
      previousMonthUtc.getUTCMonth() + 1, 
      0, 23, 59, 59, 999
    ));
    
    // For yearly: current year UTC
    this.dateYearFrom = new Date(Date.UTC(nowUtc.getUTCFullYear(), 0, 1));
    this.dateYearTo = new Date(Date.UTC(nowUtc.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
  }

  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    
    // Convert to UTC for proper comparison
    const compareDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const minCompare = new Date(Date.UTC(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate()));
    const maxCompare = new Date(Date.UTC(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate()));
    
    return compareDate >= minCompare && compareDate <= maxCompare;
  }

  setMonthAndYear(normalizedMonthAndYear: Date, datepicker: MatDatepicker<Date>) {
    // Set month range in UTC
    this.dateMonthFrom = new Date(Date.UTC(
      normalizedMonthAndYear.getFullYear(), 
      normalizedMonthAndYear.getMonth(), 
      1, 0, 0, 0, 0
    ));
    
    // Set the end of the month in UTC
    this.dateMonthTo = new Date(Date.UTC(
      normalizedMonthAndYear.getFullYear(), 
      normalizedMonthAndYear.getMonth() + 1, 
      0, 23, 59, 59, 999
    ));
    
    this.hasUserNavigatedMonth = true; // User has manually navigated
    datepicker.close();
    this.updateAllParams(); // Update URL parameters
    this.loadData();
  }


  setUsageSummaryType(usageSummaryType: UsageSummaryType) {
    if (usageSummaryType !== this.usageSummaryType) {
      this.usageSummaryType = usageSummaryType;
      this.updateAllParams(); // Update URL parameters
      this.loadData();
      this.filterUsageSummaries();
    }
  }

  toggleCompanyFilter() {
    this.companyFilter = !this.companyFilter;
    this.loadData();
  }

  loadData() {
    this.loading = true;

    let fromDate: Date;
    let toDate: Date;

    switch (this.usageSummaryType) {
      case UsageSummaryType.Day:
        fromDate = this.dateFrom;
        // Ensure dateTo is set to end of the same day as dateFrom in UTC
        this.dateTo = new Date(Date.UTC(
          this.dateFrom.getUTCFullYear(), 
          this.dateFrom.getUTCMonth(), 
          this.dateFrom.getUTCDate(), 
          23, 59, 59, 999
        ));
        toDate = this.dateTo;
        break;
      case UsageSummaryType.Month:
        fromDate = this.dateMonthFrom;
        toDate = this.dateMonthTo;
        break;
      case UsageSummaryType.Year:
        fromDate = this.dateYearFrom;
        toDate = this.dateYearTo;
        break;
      default:
        fromDate = this.dateFrom;
        toDate = this.dateTo;
        break;
    }

    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(23, 59, 59, 999);

    if (this.configService.config.envName === "dev") {
      this.generateMockUsageSummaries();
      this.filterUsageSummaries();
      this.loading = false;
    } else {
      const request = new ListUsageSummariesRequest();
      if (!this.isSituUser) {
        if (this.authService.isAuthorizedForCompanyById(this.selectedCompany?.id!)) {
          request.companyId = this.selectedCompany?.id;
        } else {
          const defaultCompany = this.authService.getDefaultCompanyId();
          if (defaultCompany) request.companyId = defaultCompany;
        }
      } else {
        // Always filter by selected company for focused dashboard view
        request.companyId = this.selectedCompany?.id;
      }
      request.pageNumber = this.viewControls.pageIndex;
      request.pageSize = this.viewControls.pageSize;
      request.fromDate = fromDate;
      request.toDate = toDate;
      request.includeAll = true;
      request.type = this.usageSummaryType;
  
      this.situAnalyticsClient.listUsageSummaries(request)
        .then((response: ListUsageSummariesResponse) => {
          this.usageSummaries = this.getLatestDataForEachCompany(response.items ?? []) ?? [];
          this.viewControls.totalItems = response.totalItems ?? 0;
          this.filterUsageSummaries();
          this.loading = false;
        }, error => {
          this.loading = false;
        });
    }
  }

  private filterUsageSummaries() {
    this.filteredUsageSummaries = this.usageSummaries.filter(x => x.usageSummaryType === this.usageSummaryType);
    
    // Set current usage summary to the company's data or first available
    if (this.filteredUsageSummaries.length > 0) {
      // Try to find the selected company's data first
      this.currentUsageSummary = this.filteredUsageSummaries.find(x => 
        x.companyId === this.selectedCompany?.id
      ) || this.filteredUsageSummaries[0];
    } else {
      this.currentUsageSummary = undefined;
    }
    
    this.sumUsageData();
  }

  private sumUsageData() {
    this.resetChartData();
    this.filteredUsageSummaries.forEach((usageSummary: UsageSummary) => {
      if ((usageSummary.productCallouts!) > 0) {
        this.chartCalloutsData.push(usageSummary.productCallouts!);
        this.chartCalloutsLabels.push(`${usageSummary.company} (${this.viewRequestsUnit})`);
      }
      
      if ((usageSummary.renderTime!) > 0) {
        this.chartRendersData.push(usageSummary.renderTime!);
        this.chartRendersLabels.push(`${usageSummary.company} (${this.renderTimeUnit})`);
      }
     
      if ((usageSummary.viewHomeRequests! + usageSummary.viewRoomRequests!) > 0) {
        this.chartViewsData.push(usageSummary.viewHomeRequests! + usageSummary.viewRoomRequests!);
        this.chartViewsLabels.push(`${usageSummary.company} (${this.viewRequestsUnit})`);
      }

      if ((usageSummary.storageConsumed!) > 0) {
        this.chartStorageData.push(Math.round((usageSummary.storageConsumed! / 1024) / 1024));
        this.chartStorageLabels.push(`${usageSummary.company} (${this.storageConsumedUnit})`);
      }
    });
    this.applyChartColors();
  }

  private resetChartData() {
    this.chartCalloutsLabels = [];
    this.chartCalloutsData = [];
    this.chartCalloutsColors = [];
    this.chartRendersLabels = [];
    this.chartRendersData = [];
    this.chartRendersColors = [];
    this.chartViewsLabels = [];
    this.chartViewsData = [];
    this.chartViewsColors = [];
    this.chartStorageLabels = [];
    this.chartStorageData = [];
    this.chartStorageColors = [];
  }

  private applyChartColors() {
    this.chartCalloutsColors = [this.generateChartColors(this.baseColorBlue, this.chartCalloutsData.length)];
    this.chartRendersColors = [this.generateChartColors(this.baseColorPink, this.chartRendersData.length)];
    this.chartViewsColors = [this.generateChartColors(this.baseColorGreen, this.chartViewsData.length)];
    this.chartStorageColors = [this.generateChartColors(this.baseColorOrange, this.chartStorageData.length)];
  }

  private generateChartColors(baseHsl: string, dataLength: number): any {
    const colors = this.generateShades(baseHsl, dataLength);
    return { backgroundColor: colors };
  }

  private generateShades(baseHsl: string, numberOfShades: number): string[] {
    const [hue, saturation, lightness] = baseHsl.match(/\d+/g)!.map(Number);
    const step = numberOfShades > 6 ? 2 : 10;
    return Array.from({ length: numberOfShades }, (_, i) => {
      const adjustedLightness = Math.max(lightness - (i * step), 10);
      return `hsl(${hue}, ${saturation}%, ${adjustedLightness}%)`;
    });
  }


  private generateMockUsageSummaries() {
    const mockData: UsageSummary[] = [];
    const numberOfMockItems = 10;

    for (let i = 0; i < numberOfMockItems; i++) {
      const mockItem = new UsageSummary({
        companyId: `company-id-${i}`,
        company: `Company ${i + 1}`,
        timestamp: new Date(),
        usageSummaryType: this.usageSummaryType,
        renderRequests: Math.floor(Math.random() * 100),
        renderQueueTime: Math.floor(Math.random() * 100),
        renderTime: Math.floor(Math.random() * 100),
        viewHomeRequests: Math.floor(Math.random() * 100),
        viewRoomRequests: Math.floor(Math.random() * 100),
        viewFacadeRequests: Math.floor(Math.random() * 100),
        viewVanityRequests: Math.floor(Math.random() * 100),
        viewPlanRequests: Math.floor(Math.random() * 100),
        viewProductRequests: Math.floor(Math.random() * 100),
        viewRenderRequests: Math.floor(Math.random() * 100),
        viewVirtualRealityRequests: Math.floor(Math.random() * 100),
        viewConnectRequests: Math.floor(Math.random() * 100),
        productCallouts: Math.floor(Math.random() * 100),
        productCalloutsProducts: Math.floor(Math.random() * 100),
        productCalloutsHomes: Math.floor(Math.random() * 100),
        storageConsumed: Math.floor(Math.random() * 100 * 122048),
        storageConsumedTotal: Math.floor(Math.random() * 100 * 1224048)
      });

      mockData.push(mockItem);
    }

    this.usageSummaries = mockData;
  }

  private getLatestDataForEachCompany(data: UsageSummary[]): UsageSummary[] {
    const latestDataMap = new Map<string, UsageSummary>();
  
    data.forEach(item => {
      const existingEntry = latestDataMap.get(item.companyId!);
      if (!existingEntry || (item.timestamp && item.timestamp > existingEntry.timestamp!)) {
        latestDataMap.set(item.companyId!, item);
      }
    });
  
    return Array.from(latestDataMap.values());
  }

  // URL Parameter Management Methods
  // No longer automatically updating URL parameters
  private updateAllParams() {
    // Method kept for compatibility but no longer modifies URL
    // URL parameters are only used for incoming deep links
  }

  // Clear URL parameters to keep URL clean
  private clearUrlParameters() {
    const urlTree = this.router.createUrlTree([], {
      queryParams: {},
      preserveFragment: false
    });
    this.router.navigateByUrl(urlTree, { replaceUrl: true });
  }

  private updateAllParamsIfNeeded() {
    // Only update URL if we're not showing current defaults
    if (this.usageSummaryType !== UsageSummaryType.Month) {
      this.updateAllParams();
      return;
    }

    const currentMonth = new Date();
    const currentMonthKey = this.formatMonthForUrl(new Date(Date.UTC(
      currentMonth.getUTCFullYear(),
      currentMonth.getUTCMonth(),
      1
    )));
    const selectedMonthKey = this.formatMonthForUrl(this.dateMonthFrom);
    
    if (selectedMonthKey !== currentMonthKey) {
      this.updateAllParams();
    }
  }

  private getOriginalCompanyId(): string | undefined {
    return this.originalCompanyId;
  }

  private formatMonthForUrl(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    return `${year}-${month}`;
  }

  private formatDateForUrl(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getPeriodParamValue(): string {
    switch (this.usageSummaryType) {
      case UsageSummaryType.Day:
        return 'daily';
      case UsageSummaryType.Year:
        return 'yearly';
      default:
        return 'monthly';
    }
  }

  private setMonthFromUrlParam(monthParam: string) {
    try {
      const [year, month] = monthParam.split('-').map(Number);
      if (year && month && month >= 1 && month <= 12) {
        this.dateMonthFrom = new Date(Date.UTC(year, month - 1, 1));
        this.dateMonthTo = new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
        return;
      }
    } catch (error) {
      // Invalid month parameter, fall back to default
    }
    
    // Fallback to default if parameter is invalid
    this.initializeUtcDates();
  }

  private setDateFromUrlParam(dateParam: string) {
    try {
      const [year, month, day] = dateParam.split('-').map(Number);
      if (year && month && day && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        this.dateFrom = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        this.dateTo = new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        return;
      }
    } catch (error) {
      // Invalid date parameter, fall back to default
    }
    
    // Fallback to default if parameter is invalid
    this.initializeUtcDates();
  }

  private setPeriodFromUrlParam(periodParam: string) {
    switch (periodParam.toLowerCase()) {
      case 'daily':
        this.usageSummaryType = UsageSummaryType.Day;
        break;
      case 'yearly':
        this.usageSummaryType = UsageSummaryType.Year;
        break;
      case 'monthly':
      default:
        this.usageSummaryType = UsageSummaryType.Month;
        break;
    }
  }

  private processUrlParameters() {
    // Wait for auth service to be ready before processing company parameters
    const checkAuthReady = () => {
      // Check if user is logged in (auth is ready)
      if (this.authService.isLoggedIn()) {
        // Process company parameter if provided
        if (this.urlCompanyParam) {
          this.setCompanyFromUrlParam(this.urlCompanyParam);
        }
      } else {
        // If auth not ready, wait a bit longer
        setTimeout(checkAuthReady, 200);
      }
    };

    // Start checking after initial delay
    setTimeout(checkAuthReady, 100);
  }

  private setCompanyFromUrlParam(companyParam: string) {
    // Check if user is authorized for this company
    if (!this.authService.isAuthorizedForCompanyById(companyParam)) {
      this.showMessage('You are not authorized to view data for the requested company.');
      // Don't redirect immediately - just show message and continue with current company
      return;
    }

    // Set the company in the state service when coming in via URL
    this.companyService.getCompanyById(companyParam)
      .then((company) => {
        if (company) {
          // Update the state with the company from URL
          this.stateService.state = {
            ...this.stateService.state,
            company
          };
          
          // Trigger data reload with new company
          setTimeout(() => {
            this.loadData();
          }, 100);
        } else {
          this.showMessage('The requested company could not be found. Showing current company data.');
        }
      })
      .catch((error) => {
        this.showMessage('Error loading company data. Showing current company data.');
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  // Copy shareable URL to clipboard
  copyShareableUrl() {
    const shareableUrl = this.generateShareableUrl();
    
    if (navigator.clipboard && window.isSecureContext) {
      // Modern approach using Clipboard API
      navigator.clipboard.writeText(shareableUrl).then(() => {
        this.showMessage('Shareable link copied to clipboard!');
      }).catch(err => {
        this.fallbackCopyTextToClipboard(shareableUrl);
      });
    } else {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(shareableUrl);
    }
  }

  private fallbackCopyTextToClipboard(text: string) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showMessage('Shareable link copied to clipboard!');
      } else {
        this.showMessage('Unable to copy link. Please copy manually from address bar.');
      }
    } catch (err) {
      this.showMessage('Unable to copy link. Please copy manually from address bar.');
    }
    
    document.body.removeChild(textArea);
  }

  // Helper method to generate shareable URLs
  private generateShareableUrl(month?: Date, companyId?: string, period?: UsageSummaryType): string {
    const baseUrl = window.location.origin + this.router.url.split('?')[0];
    const params = new URLSearchParams();
    
    // Always add current company ID for sharing
    const targetCompanyId = companyId || this.selectedCompany?.id;
    if (targetCompanyId) {
      params.set(this.CompanyParamName, targetCompanyId);
    }
    
    // Add period parameter
    const targetPeriod = period || this.usageSummaryType;
    
    if (targetPeriod === UsageSummaryType.Day) {
      params.set(this.PeriodParamName, 'daily');
      // Add specific date for daily view
      const targetDate = this.dateFrom;
      const dateKey = this.formatDateForUrl(targetDate);
      params.set(this.DateParamName, dateKey);
    } else if (targetPeriod === UsageSummaryType.Month) {
      params.set(this.PeriodParamName, 'monthly');
      // Add month parameter for monthly view
      const targetMonth = month || this.dateMonthFrom;
      const targetMonthKey = this.formatMonthForUrl(targetMonth);
      params.set(this.MonthParamName, targetMonthKey);
    } else {
      params.set(this.PeriodParamName, this.getPeriodParamValue());
    }
    
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }
}